import React, { useCallback, useEffect, useState, useRef } from "react";
import Pagination from "@mui/material/Pagination";
import { API_URLS } from "../../../../apiUrl";
import axiosInstance from "../../../../components/axiosInstance";
import { toast, ToastContainer } from "react-toastify";

interface User {
  primary_id: number;
  municipality_name: string;
  login_user_name: string;
  admin_name: string;
  email: string;
  status: number;
  municipality_id: any;
  role_id: any;
}

// ─── Info Modal Component ─────────────────────────────────────────────────────
interface InfoModalProps {
  title: string;
  content: string;
  onClose: () => void;
}

const InfoModal: React.FC<InfoModalProps> = ({ title, content, onClose }) => {
  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      onClick={handleBackdrop}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div className="bg-white rounded-xl border border-gray-200 w-[400px] max-w-[90vw] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 14 14" stroke="currentColor" strokeWidth={1.6}>
              <line x1="2" y1="2" x2="12" y2="12" />
              <line x1="12" y1="2" x2="2" y2="12" />
            </svg>
          </button>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-gray-700 break-words">{content}</p>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Truncated Text Component with Info Button ────────────────────────────────
interface TruncatedTextProps {
  text: string;
  maxLength?: number;
}

const TruncatedText: React.FC<TruncatedTextProps> = ({ text, maxLength = 10 }) => {
  const [showInfoModal, setShowInfoModal] = useState(false);
  
  const needsTruncation = text && text.length > maxLength;
  const displayText = needsTruncation ? `${text.substring(0, maxLength)}...` : text;

  if (!text || text === "-") {
    return <span className="text-sm">{text || "-"}</span>;
  }

  return (
    <>
      <div className="flex items-center gap-1">
        <span className="text-sm">{displayText}</span>
        {needsTruncation && (
          <button
            onClick={() => setShowInfoModal(true)}
            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition text-xs font-bold"
            title="View full text"
          >
            i
          </button>
        )}
      </div>
      
      {showInfoModal && (
        <InfoModal
          title="Full Text"
          content={text}
          onClose={() => setShowInfoModal(false)}
        />
      )}
    </>
  );
};

// ─── Edit Modal ───────────────────────────────────────────────────────────────
interface EditModalProps {
  user: User;
  onClose: () => void;
  onSaved: () => void;
}

const EditModal: React.FC<EditModalProps> = ({ user, onClose, onSaved }) => {
  const [form, setForm] = useState({
    admin_name: user.admin_name,
    login_user_name: user.login_user_name,
    email: user.email || "",
    status: user.status,
    password: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = React.useRef(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const initials = user.admin_name
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join("");

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!form.admin_name.trim()) {
      newErrors.admin_name = "Full name is required";
    } else if (form.admin_name.length < 2) {
      newErrors.admin_name = "Minimum 2 characters required";
    }

    if (!form.login_user_name.trim()) {
      newErrors.login_user_name = "Username is required";
    } else if (form.login_user_name.length < 2) {
      newErrors.login_user_name = "Minimum 2 characters required";
    }

    if (form.email.trim() && !emailRegex.test(form.email)) {
      newErrors.email = "Invalid email address";
    }

    if (form.password.trim()) {
      if (form.password.length < 6) {
        newErrors.password = "Minimum 6 characters required";
      } else if (!/[A-Z]/.test(form.password)) {
        newErrors.password = "1 uppercase required";
      } else if (!/\d/.test(form.password)) {
        newErrors.password = "1 number required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === "admin_name" && value.length > 100) return;
    if (name === "login_user_name" && value.length > 100) return;
    if (name === "email" && value.length > 100) return;
    if (name === "password" && value.length > 20) return;

    setForm((prev) => ({
      ...prev,
      [name]: name === "status" ? Number(value) : value,
    }));

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

const handleSubmit = async () => {
    if (savingRef.current) return;

    const isValid = validate();
    if (!isValid) return;

    savingRef.current = true;
    setSaving(true);

    try {
      const payload: any = {
        id: user.primary_id,
        login_user_name: form.login_user_name,
        admin_name: form.admin_name,
        municipality_name: user.municipality_name,
        email: form.email || "",
        role_id: user.role_id,
        ward_id: [],
        municipality_id: user.municipality_id,
      };

      if (form.password.trim()) payload.password = form.password;

      const response = await axiosInstance.post(
        API_URLS.updateCashierandcollector,
        payload
      );

      toast.success(response?.data?.message || "Updated successfully!");
      onSaved();
      onClose();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Update failed";
      toast.error(errorMessage);
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
};

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      onClick={handleBackdrop}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div className="bg-white rounded-xl border border-gray-200 w-[520px] max-w-[95vw] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center text-sm font-medium text-blue-600">
              {initials}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {user.admin_name}
              </p>
              <p className="text-xs text-gray-400">
                Cashier · {user.municipality_name}
              </p>
            </div>
          </div>
          <button onClick={onClose}>✕</button>
        </div>

        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-gray-500 mb-1.5">
              Full name *
            </label>
            <input
              name="admin_name"
              value={form.admin_name}
              onChange={handleChange}
              autoComplete="off"
              className={`w-full px-3 py-2 border rounded-lg ${
                errors.admin_name ? "border-red-500" : "border-gray-200"
              }`}
            />
            {errors.admin_name && (
              <p className="text-red-500 text-xs mt-1">{errors.admin_name}</p>
            )}
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1.5">
              Username *
            </label>
            <input
              name="login_user_name"
              value={form.login_user_name}
              onChange={handleChange}
              autoComplete="off"
              className={`w-full px-3 py-2 border rounded-lg ${
                errors.login_user_name ? "border-red-500" : "border-gray-200"
              }`}
            />
            {errors.login_user_name && (
              <p className="text-red-500 text-xs mt-1">
                {errors.login_user_name}
              </p>
            )}
          </div>

          <div className="col-span-2">
            <label className="text-xs text-gray-500 mb-1.5">
              Email (Optional)
            </label>
            <input
              name="email"
              value={form.email}
              onChange={handleChange}
              autoComplete="off"
              className={`w-full px-3 py-2 border rounded-lg ${
                errors.email ? "border-red-500" : "border-gray-200"
              }`}
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          <div className="col-span-2">
            <label className="text-xs text-gray-500 mb-1.5">
              New Password
            </label>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={form.password}
              onChange={handleChange}
              autoComplete="new-password"
              className={`w-full px-3 py-2 border rounded-lg ${
                errors.password ? "border-red-500" : "border-gray-200"
              }`}
            />
            {errors.password && (
              <p className="text-red-500 text-xs mt-1">
                {errors.password}
              </p>
            )}
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              {/* Password toggle icon */}
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
  <button
    onClick={onClose}
    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
  >
    Cancel
  </button>
  <button
    onClick={handleSubmit}
    disabled={saving}
    className={`px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 ${
      saving
        ? "bg-gray-400 cursor-not-allowed"
        : "bg-blue-600 hover:bg-blue-700 active:bg-blue-800"
    }`}
  >
    {saving ? (
      <span className="flex items-center gap-2">
        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Saving...
      </span>
    ) : (
      "Update Cashier"
    )}
  </button>
</div>
      </div>
    </div>
  );
};

// ─── Main List ────────────────────────────────────────────────────────────────
const AccountentList: React.FC = () => {
  const [userList, setUserList] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [displayList, setDisplayList] = useState<User[]>([]);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const isEditingRef = useRef(false);
  const previousSearchQueryRef = useRef(searchQuery);

  const fetchUsers = useCallback(async (currentPage: number) => {
    if (isEditingRef.current) return;
    
    try {
      const response = await axiosInstance.post(API_URLS.usercollector, {
        role_name: "cashier",
        page: currentPage,
        limit: 50,
      });
      const rows = response.data?.data?.rows || [];
      const pagination = response.data?.data?.pagination;
      setUserList(rows);
      setDisplayList(rows);
      setTotalPages(pagination?.lastPage || 1);
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch users";
      toast.error(msg, { position: "top-right", autoClose: 3000 });
    }
  }, []);

  useEffect(() => {
    if (isEditingRef.current) {
      return;
    }

    if (previousSearchQueryRef.current === searchQuery) {
      return;
    }
    previousSearchQueryRef.current = searchQuery;

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (!searchQuery.trim()) {
      setIsSearching(false);
      setDisplayList(userList); 
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await axiosInstance.post(API_URLS.usercollector, {
          role_name: "cashier",
          search: searchQuery.trim(),
          limit: 50,
          page: 1,
        });
        setDisplayList(response.data?.data?.rows || []);
      } catch (error: any) {
        toast.error(error?.message || "Search failed", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, userList]);

  useEffect(() => {
    if (!searchQuery.trim() && !isEditingRef.current) {
      fetchUsers(page);
    }
  }, [page, searchQuery, fetchUsers]);

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleEdit = (user: User) => {
    isEditingRef.current = true;
    setEditUser(user);
  };

  const handleSaveComplete = async () => {
    isEditingRef.current = false;
    
    if (searchQuery.trim()) {
      setIsSearching(true);
      try {
        const response = await axiosInstance.post(API_URLS.usercollector, {
          role_name: "cashier",
          search: searchQuery.trim(),
          limit: 50,
          page: 1,
        });
        setDisplayList(response.data?.data?.rows || []);
      } catch (error: any) {
        toast.error(error?.message || "Search failed", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setIsSearching(false);
      }
    } else {
      await fetchUsers(page);
    }
  };

  const handleCloseModal = () => {
    setEditUser(null);
    setTimeout(() => {
      isEditingRef.current = false;
    }, 100);
  };

  return (
    <div className="container mx-auto p-6">
      <div className="flex gap-5 mb-6 items-center">
        <h1 className="text-2xl font-semibold">Accountant List</h1>
        <div className="ml-auto w-4/12">
          <input
            type="text"
            placeholder="Search by name or username"
            autoComplete="off"
            name="search-input"
            id="search-input"
            className="border px-3 py-2 rounded-lg w-full"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            onFocus={(e) => {
              e.target.autocomplete = "off";
            }}
          />
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-lg shadow-lg border">
        <table className="min-w-full table-auto">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="py-2.5 px-3 text-left font-semibold text-gray-600">
                Municipality
              </th>
              <th className="py-2.5 px-3 text-left font-semibold text-gray-600">
                Name
              </th>
              <th className="py-2.5 px-3 text-left font-semibold text-gray-600">
                Username
              </th>
              <th className="py-2.5 px-3 text-left font-semibold text-gray-600">
                Email
              </th>
              <th className="py-2.5 px-3 text-left font-semibold text-gray-600">
                Status
              </th>
              <th className="py-2.5 px-3 text-left font-semibold text-gray-600">
                Edit
              </th>
            </tr>
          </thead>
          <tbody>
            {isSearching ? (
              <tr>
                <td colSpan={6} className="text-center py-4">
                  Searching...
                </td>
              </tr>
            ) : displayList.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-4">
                  No Data Found
                </td>
              </tr>
            ) : (
              displayList.map((item) => (
                <tr
                  key={item.primary_id}
                  className="border-b hover:bg-gray-50 transition"
                >
                  <td className="py-3 px-3 text-sm">
                    <TruncatedText text={item.municipality_name} maxLength={50} />
                  </td>
                  <td className="py-3 px-3 text-sm">
                    <TruncatedText text={item.admin_name} maxLength={20} />
                  </td>
                  <td className="py-3 px-3 text-sm">
                    <TruncatedText text={item.login_user_name} maxLength={20} />
                  </td>
                  <td className="py-3 px-3 text-sm">
                    <TruncatedText text={item.email || "-"} maxLength={25} />
                  </td>
                  <td className="py-3 px-3 text-sm">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        item.status === 1
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {item.status === 1 ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="py-2.5 px-3">
                    <button
                      title="Edit"
                      onClick={() => handleEdit(item)}
                      className="w-7 h-7 flex items-center justify-center rounded-full text-blue-500 border border-blue-200 hover:bg-blue-50 hover:text-blue-700 transition"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!searchQuery.trim() && (
        <div className="flex justify-center mt-6">
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </div>
      )}

      {editUser && (
        <EditModal
          user={editUser}
          onClose={handleCloseModal}
          onSaved={handleSaveComplete}
        />
      )}
      <ToastContainer />
    </div>
  );
};

export default AccountentList;